import { useEffect, useState } from "react";

import { apiClient } from "@/services/apiClient";

const isPresent = (value: string | null | undefined): value is string =>
  value != null && value.trim() !== "";

/**
 * The authenticated route for one leg's awarded professional, or null when
 * there is nothing to fetch. Pulled out of the effect so the effect itself is
 * a flat fetch-and-revoke with no conditional pyramid.
 */
function avatarPath(
  quoteRequestId: string | undefined,
  legId: string | undefined,
  storedProfileImageUrl: string | null | undefined,
): string | null {
  if (!isPresent(quoteRequestId) || !isPresent(legId) || !isPresent(storedProfileImageUrl)) {
    return null;
  }
  return `/me/quote-requests/${encodeURIComponent(
    quoteRequestId,
  )}/legs/${encodeURIComponent(legId)}/awarded-professional/avatar-image`;
}

/**
 * Loads the avatar of the professional awarded ONE leg of an RFQ.
 *
 * Same reason as useAssignedProfessionalAvatarObjectUrl: profile images live
 * in private blob storage and cannot be used as a raw img src, so the bytes
 * come over an authenticated request. Separate hook rather than a third
 * `requestSource` on that one, because the route is shaped differently — it is
 * per LEG, since a two-discipline RFQ has one awarded professional per
 * discipline and no single "the" professional.
 *
 * `storedProfileImageUrl` is only a presence flag and a cache key; the server
 * decides what it actually serves.
 */
export function useAwardedProfessionalAvatarObjectUrl(
  quoteRequestId: string | undefined,
  legId: string | undefined,
  storedProfileImageUrl: string | null | undefined,
): string | null {
  const [src, setSrc] = useState<string | null>(null);
  const path = avatarPath(quoteRequestId, legId, storedProfileImageUrl);

  useEffect(() => {
    if (path == null) {
      setSrc(null);
      return;
    }
    const controller = new AbortController();

    void (async () => {
      try {
        const res = await apiClient.get(path, {
          responseType: "blob",
          signal: controller.signal,
        });
        if (!controller.signal.aborted) setSrc(URL.createObjectURL(res.data));
      } catch {
        if (!controller.signal.aborted) setSrc(null);
      }
    })();

    return () => {
      controller.abort();
      // Revoked on the way out, or every re-award leaks an object URL for the
      // lifetime of the page.
      setSrc((prev) => {
        if (prev != null) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [path]);

  return src;
}
